import {
  Box,
  Flex,
  Grid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import {
  ArrowLongRightIcon,
  ArrowTurnDownRightIcon,
  ChevronRightIcon
} from '@heroicons/react/16/solid'
import {
  GreyGrey30,
  GreyGrey60,
  GreyGrey80,
  Lato13Regular,
  Lato14Regular,
  Lato16Bold
} from '@sequencehq/design-tokens'
import {
  TablePhase,
  TableProductInformation,
  useSchedulePricingSection
} from './useSchedulePricingSection'
import { Link, useNavigate } from 'react-router-dom'
import { useCallback, useState } from 'react'
import {
  SequenceKebabMenu,
  SequenceMenuItem,
  Skeleton
} from '@sequencehq/core-components'
import fill from 'lodash/fill'
import { SectionError } from '../../common/SectionError'

const PhaseDateSummary = (props: {
  dateSummary: TablePhase['dateSummary']
}) => {
  if (!props.dateSummary.endDate || props.dateSummary.isAfterMilestonePhase) {
    return <Box>{props.dateSummary.duration}</Box>
  }

  return (
    <Flex alignItems="center" gap="4px">
      <Box>{props.dateSummary.startDate}</Box>
      <ArrowLongRightIcon height="16px" width="16px" color="inherit" />
      <Box>{props.dateSummary.endDate}</Box>
    </Flex>
  )
}

const ProductPriceSummary = (props: {
  priceSummary: TableProductInformation['summary']
}) => {
  if (!props.priceSummary.totalAfterDiscount) {
    return (
      <Box>
        {props.priceSummary.total} / {props.priceSummary.billingFrequency}
      </Box>
    )
  }

  return (
    <Box>
      <Box
        as="span"
        textDecoration="line-through"
        color={GreyGrey60}
        marginRight="4px"
      >
        {props.priceSummary.total}
      </Box>
      {props.priceSummary.total} / {props.priceSummary.billingFrequency}
    </Box>
  )
}

const PriceKebabMenu = (props: {
  priceId: string
}) => {
  return (
    <SequenceKebabMenu
      variant="ghost"
      alignment="bottom-right"
      buttonSize="small"
      items={[
        ({ setIsOpen }) => (
          <Link key="view-price" to={`./price/${props.priceId}`}>
            <SequenceMenuItem
              uuid="view-schedule"
              label="View price"
              data-testid={`schedule.prices.${props.priceId}.menuItem.view`}
              onClick={() => setIsOpen?.(false)}
            />
          </Link>
        )
      ]}
    />
  )
}

const PhaseTable = (props: {
  phase: TablePhase
  mountExpanded?: boolean
}) => {
  const [expanded, setExpanded] = useState(props.mountExpanded ?? false)
  const navigate = useNavigate()
  const onRowClick = useCallback(
    (priceId: string) => {
      navigate(`./price/${priceId}`)
    },
    [navigate]
  )

  return (
    <Box padding="8px 0">
      <Flex
        gap="8px"
        alignContent="center"
        cursor="pointer"
        onClick={() => setExpanded(!expanded)}
        userSelect="none"
      >
        <Box
          transform={expanded ? 'rotateZ(90deg)' : 'rotateZ(0)'}
          transition="transform 250ms ease-in-out"
        >
          <ChevronRightIcon height="16px" width="16px" />
        </Box>
        <Box {...Lato14Regular} color={GreyGrey80}>
          {props.phase.name ? (
            props.phase.name
          ) : (
            <PhaseDateSummary dateSummary={props.phase.dateSummary} />
          )}
        </Box>
        {props.phase.name && (
          <Box
            {...Lato13Regular}
            color={GreyGrey60}
            position="relative"
            top="1px"
          >
            <PhaseDateSummary dateSummary={props.phase.dateSummary} />
          </Box>
        )}
      </Flex>
      <Box
        height={expanded ? 'fit-content' : '0px'}
        css={{
          /**
            This has limited browser support for now - basically chromium only,
            but it's just a bit of bonus bacon that transitions to height nicely.
          */
          interpolateSize: 'allow-keywords'
        }}
        transition={'height 0.25s ease-in-out'}
        overflow="hidden"
      >
        <TableContainer
          borderRadius="lg"
          overflow="hidden"
          border={`1px solid ${GreyGrey30}`}
          mt="16px"
          width="100%"
          boxShadow="0px 0px 2px 0px rgba(0, 0, 0, 0.02), 0px 1px 1px 0px rgba(0, 0, 0, 0.05)"
        >
          <Table variant="v2" width="100%">
            <Thead userSelect="none">
              <Tr>
                <Th>Description</Th>
                <Th width="148px" textAlign="right">
                  {props.phase.phaseHasDiscount ? 'Discount' : ''}
                </Th>
                <Th width="350px" textAlign="right">
                  Amount
                </Th>
                <Th width="52px"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {props.phase.productsWithNoMinimum.map(product => (
                <Tr
                  key={product.id}
                  onClick={() => onRowClick(product.priceId)}
                >
                  <Td>{product.name}</Td>
                  <Td textAlign="right">{product.summary.discount}</Td>
                  <Td textAlign="right">
                    <ProductPriceSummary priceSummary={product.summary} />
                  </Td>
                  <Td padding="0 12px !important">
                    <PriceKebabMenu priceId={product.priceId} />
                  </Td>
                </Tr>
              ))}
              {props.phase.minimum && (
                <Tr pointerEvents="none">
                  <Td>Minimum commitment</Td>
                  <Td></Td>
                  <Td textAlign="right">
                    {props.phase.minimum.summary.minimumAmount} /{' '}
                    {props.phase.minimum.summary.billingFrequency}
                  </Td>
                  <Td></Td>
                </Tr>
              )}
              {props.phase.minimum?.products.map(product => (
                <Tr
                  key={product.id}
                  onClick={() => onRowClick(product.priceId)}
                >
                  <Td>
                    <Flex gap="8px">
                      <ArrowTurnDownRightIcon
                        color={GreyGrey60}
                        height="16px"
                        width="16px"
                      />
                      {product.name}
                    </Flex>
                  </Td>
                  <Td textAlign="right">{product.summary.discount}</Td>
                  <Td textAlign="right">
                    <ProductPriceSummary priceSummary={product.summary} />
                  </Td>
                  <Td padding="0 12px !important">
                    <PriceKebabMenu priceId={product.priceId} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  )
}

export const SchedulePricingSection = () => {
  const hook = useSchedulePricingSection()

  if (hook.isLoading) {
    return (
      <Box>
        <Box {...Lato16Bold} marginBottom="16px">
          Pricing
        </Box>
        <Skeleton height="24px" width="240px" />
        <Flex
          gap="8px"
          marginBottom="24px"
          marginTop="16px"
          flexDirection="column"
        >
          {fill(Array(3), null).map((_, idx) => (
            <Skeleton height="36px" width="100%" key={idx} />
          ))}
        </Flex>
        <Skeleton height="24px" width="120px" />
        <Flex gap="8px" marginTop="16px" flexDirection="column">
          {fill(Array(4), null).map((_, idx) => (
            <Skeleton height="36px" width="100%" key={idx} />
          ))}
        </Flex>
      </Box>
    )
  }

  if (hook.isError) {
    return (
      <Box>
        <Box {...Lato16Bold} marginBottom="16px">
          Pricing
        </Box>
        <Box
          borderRadius="lg"
          padding="16px"
          overflow="hidden"
          border={`1px solid ${GreyGrey30}`}
        >
          <SectionError
            size="medium"
            text="An error occurred when loading the pricing information"
          />
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Box {...Lato16Bold} marginBottom="16px">
        Pricing
      </Box>
      <Grid gridTemplateRows="auto" gridRowGap="8px">
        {hook.phases?.map(phase => (
          <PhaseTable
            key={phase.id}
            phase={phase}
            mountExpanded={hook.initialPhaseToExpand === phase.id}
          />
        ))}
      </Grid>
    </Box>
  )
}
